<template>
  <div>
    <div
      v-if="highlightedOrder"
      class="pickup-order-ready-overlay"
    >
      <div class="pickup-order-ready-content">
        <span
          v-if="highlightedOrder && highlightedOrder.customer && highlightedOrder.customer.id > 1 && highlightedOrder.customer.name && highlightedOrder.customer.name.toUpperCase() !== 'NÃO INFORMADO'"
          class="pickup-order-ready-customer"
        >{{ getFirstName(highlightedOrder.customer.name) }}</span>
        <span class="pickup-order-ready-title">seu pedido está pronto</span>
        <span class="pickup-order-ready-id">{{ highlightedOrder.id.toString().slice(-3) }}</span>
      </div>
    </div>
    <section class="p-1">
      <header class="pb-1 d-flex justify-content-between align-items-start">
        <div class="d-flex column align-items-center">
          <span class="brand-logo mr-1">
            <b-img
              id="logo"
              :src="style ? style.reducedHeaderImage : ''"
              alt="logo"
              fluid
              class="rounded"
            />
          </span>
          <h1 class="text-dark h1 mb-0">
            {{ $t('Retirada') }}
          </h1>
        </div>
        <div v-if="filter.storeId">
          <b-avatar
            size="40"
            icon="wifi"
            variant="light-primary"
            badge
            class="badge-minimal ml-1"
            :badge-variant="preparationWebSocketConnected ? 'success' : 'danger'"
            :title="$t('Indica o status da conexão com a atualização dos pedidos de preparação')"
          />
          <b-button
            variant="link-primary"
            :title="$t('Atualizar lista de pedidos')"
            @click="getData"
          >
            <b-icon
              icon="arrow-clockwise"
              font-scale="1"
            />
          </b-button>
          <b-button
            variant="link-primary"
            :title="$t('Testar som')"
            @click="testSound"
          >
            <b-icon
              icon="volume-up"
              font-scale="1"
            />
          </b-button>
        </div>
        <div style="min-width: 300px">
          <template v-if="!filter.storeId">
            <e-store-combo
              v-model="filter.storeId"
              :disabled="!ready"
              :required="false"
              :show-label="false"
              :clearable="true"
              :placeholder="ready ? $t('Selecione') : $t('Aguarde...')"
              @input="getData"
            />
          </template>
          <template v-else>
            <div
              class="store-label"
              style="cursor:pointer; min-height: 38px; display: flex; align-items: center; padding: 0 12px; background: #f8f9fa; font-size: 1.5rem;"
              :title="$t('Clique para alterar a loja')"
              @click="filter.storeId = null"
            >
              {{ store.tradingName }}
            </div>
          </template>
        </div>
      </header>
      <div
        v-if="!filter.storeId"
        class="text-center"
      >
        <span class="h2"> {{ needSearchMessage }}</span>
      </div>
      <div v-else>
        <div class="kanban-board d-flex">
          <div class="kanban-column flex-fill mr-25">
            <h1 class="text-center kanban-title kanban-pending">
              Aguardando
            </h1>
            <div
              v-for="order in aguardandoOrders"
              :key="order.id"
              class="pickup-order"
            >
              <span class="pickup-order-id">{{ order.id.toString().slice(-3) }}</span>
              <span
                v-if="order.customer.id > 1 && order.customer.name && order.customer.name.toUpperCase() !== 'NÃO INFORMADO'"
                class="pickup-customer"
              > {{ getFirstName(order.customer.name) }}</span>
            </div>
          </div>
          <div class="kanban-column flex-fill mr-25">
            <h1 class="text-center kanban-title kanban-preparing">
              Preparando
            </h1>
            <div
              v-for="order in preparandoOrders"
              :key="order.id"
              class="pickup-order"
            >
              <span class="pickup-order-id">{{ order.id.toString().slice(-3) }}</span>
              <span
                v-if="order.customer.id > 1 && order.customer.name && order.customer.name.toUpperCase() !== 'NÃO INFORMADO'"
                class="pickup-customer"
              > {{ getFirstName(order.customer.name) }}</span>
            </div>
          </div>
          <div class="kanban-column flex-fill">
            <h1 class="text-center kanban-title kanban-completed">
              Pronto
            </h1>
            <div
              v-for="order in prontoOrders"
              :key="order.id"
              class="pickup-order"
            >
              <span class="pickup-order-id">{{ order.id.toString().slice(-3) }}</span>
              <span
                v-if="order.customer.id > 1 && order.customer.name && order.customer.name.toUpperCase() !== 'NÃO INFORMADO'"
                class="pickup-customer"
              > {{ getFirstName(order.customer.name) }}</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { BIcon, BButton, BAvatar, BImg } from 'bootstrap-vue'
import { app } from '@/mixins'
import { mapActions, mapGetters, mapState } from 'vuex'
import { EStoreCombo } from '@/views/components'
// eslint-disable-next-line global-require
const alarmSound = new Audio(require('@/assets/audio/echope_alert.mp3'))

export default {
  components: {
    BIcon,
    BButton,
    EStoreCombo,
    BAvatar,
    BImg,
  },
  mixins: [app],
  data() {
    return {
      highlightedOrder: null,
      highlightTimeout: null,
      highlightQueue: [],
      isHighlighting: false,
      now: Date.now(),
      nowIntervalId: null,
      wsReconnectIntervalId: null,
    }
  },
  computed: {
    ...mapState('app', ['stores']),
    ...mapGetters('pages/sale/preparationDashboard', [
      'orders',
      'ready',
      'preparationWebSocketConnected',
    ]),
    ...mapState('pages/sale/preparationDashboard', ['filter', 'lastFetch']),
    counterOrders() {
      return this.orders.filter(o => o.pickupPoint === 'Counter')
    },
    aguardandoOrders() {
      return this.counterOrders.filter(o => o.preparationStatus === 'Pending')
    },
    preparandoOrders() {
      return this.counterOrders.filter(o => o.preparationStatus === 'Preparing')
    },
    prontoOrders() {
      // Exibe apenas pedidos concluídos nos últimos 5 minutos, ordenados por preparationStatusDate decrescente
      const {now} = this
      const fiveMinutes = 5 * 60 * 1000
      return this.counterOrders
        .filter(o => {
          if (o.preparationStatus !== 'Done') return false
          const dateStr = o.preparationStatusDate || o.PreparationStatusDate
          if (!dateStr) return false
          const date = new Date(dateStr)
          return now - date.getTime() <= fiveMinutes
        })
        .slice()
        .sort((a, b) => {
          const aDate = a.preparationStatusDate || a.PreparationStatusDate
          const bDate = b.preparationStatusDate || b.PreparationStatusDate
          const dateA = aDate ? new Date(aDate) : new Date(0)
          const dateB = bDate ? new Date(bDate) : new Date(0)
          return dateB - dateA
        })
    },
    needSearchMessage() {
      return this.$t('Selecione a loja para listar os pedidos')
    },
    store() {
      // eslint-disable-next-line eqeqeq
      return this.stores.find(store => store.id == this.filter.storeId)
    },
  },
  watch: {
    prontoOrders: {
      handler(newOrders, oldOrders) {
        if (!oldOrders) return
        const newReady = newOrders.find(
          o => !oldOrders.some(old => old.id === o.id)
        )
        if (newReady) {
          this.highlightOrder(newReady)
        }
      },
      deep: true,
    },
    preparationWebSocketConnected(val) {
      // Se desconectou, inicia o intervalo de reconexão
      if (!val && !this.wsReconnectIntervalId) {
        this.wsReconnectIntervalId = setInterval(() => {
          this.getData()
        }, 60000)
      }
      // Se reconectou, limpa o intervalo
      if (val && this.wsReconnectIntervalId) {
        clearInterval(this.wsReconnectIntervalId)
        this.wsReconnectIntervalId = null
      }
    },
  },
  async mounted() {
    await this.fetchPreparationWebSocketConfig()
    if (!this.preparationWebSocketConnected) {
      await this.connectToPreparationWebSocket()
    }
    await this.joinPreparationChannel()
    this.nowIntervalId = setInterval(() => {
      this.now = Date.now()
    }, 30000)
  },
  methods: {
    ...mapActions('pages/sale/preparationDashboard', [
      'fetchPreparationOrders',
      'fetchPreparationWebSocketConfig',
      'connectToPreparationWebSocket',
      'joinPreparationChannel',
      'setFilter',
      'updatePreparationStatus',
    ]),
    async getData() {
      try {
        await this.fetchPreparationOrders()
        await this.joinPreparationChannel()
      } catch (error) {
        this.$toast.error(error.message || 'Erro ao buscar pedidos')
      }
    },
    testSound() {
      alarmSound.play()
    },
    getFirstName(name) {
      if (!name) return ''
      return name.split(' ')[0]
    },
    highlightOrder(order) {
      this.highlightQueue.push(order)
      this.processHighlightQueue()
    },

    processHighlightQueue() {
      if (this.isHighlighting || this.highlightQueue.length === 0) return
      this.isHighlighting = true
      const order = this.highlightQueue.shift()
      this.highlightedOrder = order
      alarmSound.play()
      if (this.highlightTimeout) clearTimeout(this.highlightTimeout)
      this.highlightTimeout = setTimeout(() => {
        this.highlightedOrder = null
        this.isHighlighting = false
        this.processHighlightQueue()
      }, 8000)
    },
    beforeDestroy() {
      if (this.nowIntervalId) {
        clearInterval(this.nowIntervalId)
      }
      if (this.wsReconnectIntervalId) {
        clearInterval(this.wsReconnectIntervalId)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.pickup-order-ready-title {
  color: #fff;
  font-size: 3rem;
  font-weight: 500;
  margin-bottom: 2rem;
  text-shadow: 1px 1px 6px #333;
}
.pickup-order-ready-customer {
  color: #fff;
  font-size: 4rem;
  font-weight: 400;
  margin-top: 2rem;
  text-shadow: 1px 1px 6px #333;
}
.brand-logo {
  box-sizing: border-box;
  width: 35px;
}
.kanban-title {
  color: #fff;
  padding: 0.5rem 0;
  border-radius: 8px 8px 0 0;
  font-size: 2.5rem;
  margin-bottom: 1.5rem;
}
.kanban-pending {
  background-color: #b8c2cc;
}
.kanban-preparing {
  background-color: #ffc107;
}
.kanban-completed {
  background-color: #4caf50;
}
.kanban-board {
  min-height: 60vh;
  width: 100vw;
  margin-left: calc(-50vw + 50%);
  margin-right: calc(-50vw + 50%);
  display: flex;
}
.kanban-column {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 5px;
  flex: 0 0 33.3333%;
  min-width: 0;
  max-width: 33.3333%;
  margin-right: 10px;
  box-sizing: border-box;
}
.kanban-column:last-child {
  margin-right: 0;
}
.pickup-order {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1rem 1rem;
  margin: 1rem 0;
  font-size: 3rem;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  color: #333;
}
.pickup-order-id {
  font-weight: bold;
  font-size: 6rem;
  margin-right: 2rem;
  line-height: 1;
}
.pickup-customer {
  font-size: 3rem;
}

.pickup-order-ready-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: #4caf50;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: fadeIn 0.3s;
}
.pickup-order-ready-content {
  display: flex;
  flex-direction: column;
  align-items: center;
}
.pickup-order-ready-id {
  color: #fff;
  font-size: 24rem;
  font-weight: bold;
  text-shadow: 2px 2px 8px #333;
}
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
</style>
